/* POST /clients/{slug}/properties/extract — measured identity fields + conflicts.
 *
 * Types mirror tools/page_fields.py. Each field carries an explicit status:
 * `observed` (a value was visibly present), `not-observed` (the page was read but
 * the field was absent), or `unverifiable` (the page could not be fetched/read —
 * JS-blocked, platform-blocked, or extraction unavailable). The connector never
 * fabricates: an unverifiable page's fields are all `unverifiable`, and conflicts
 * are derived from OBSERVED values only.
 */
import { request } from './client.ts';

export type FieldStatus = 'observed' | 'not-observed' | 'unverifiable';

export interface IdentityField {
  status: FieldStatus;
  value: string | string[] | null;
  extractedVia: string | null;
  confidence: number | null;
  asOf: string;
}

export interface PageFieldRecord {
  propertyUrl: string;
  status: 'extracted' | 'unverifiable';
  identityFields: Record<string, IdentityField>;
  receipt: { reason?: string | null; fetchTier?: string | null; [k: string]: unknown };
}

export interface FieldConflict {
  type: string;
  field: string;
  values: Array<{ value: string | string[]; source: string; asOf: string | null; confidence: number | null }>;
}

export interface ExtractionCoverage {
  requested: number;
  extracted: number;
  unverifiable: number;
  truncatedToCap: boolean;
  cap: number;
}

export interface ExtractResponse {
  available: boolean;
  records: PageFieldRecord[];
  conflicts: FieldConflict[];
  coverage: ExtractionCoverage;
}

/** POST the chosen property URLs for measured extraction. One explicit call per
 * AM action — the server caps/de-dupes and the connector caches complete
 * results upstream, so this is cheap to re-run. */
export function extractProperties(slug: string, urls: string[]): Promise<ExtractResponse> {
  return request<ExtractResponse>(`/clients/${encodeURIComponent(slug)}/properties/extract`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ urls }),
  });
}
